import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  fornitoriGetCentro,
  fornitoriSaveCentro,
  fornitoriFetch,
} from '../services/fornitoriService';
import '../styles/fornitoriTable.css';

const FornitoriTable = ({ cantiereId }) => {
  const [rows, setRows] = useState([]);
  const [totaleImponibile, setTotaleImponibile] = useState(0);
  const [totaleConIva, setTotaleConIva] = useState(0);
  const [totalePagato, setTotalePagato] = useState(0);
  const [centroCosto, setCentroCosto] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const res = await fornitoriFetch(cantiereId, force);
      const data = res.data || [];
      if (data.length === 0) {
        setRows([]);
        setTotaleImponibile(0);
        setTotaleConIva(0);
        setTotalePagato(0);
        setMessage('Nessun fornitore trovato per il centro di costo.');
        return;
      }
      const parsed = data.map((r) => ({
        ...r,
        imponibile_totale: Number(r.imponibile_totale) || 0,
        totale_con_iva: Number(r.totale_con_iva) || 0,
        totale_pagato: Number(r.totale_pagato) || 0,
      }));
      setRows(parsed);
      const imp = parsed.reduce((s, r) => s + r.imponibile_totale, 0);
      const conIva = parsed.reduce((s, r) => s + r.totale_con_iva, 0);
      const pag = parsed.reduce((s, r) => s + r.totale_pagato, 0);
      setTotaleImponibile(imp);
      setTotaleConIva(conIva);
      setTotalePagato(pag);
      setMessage('');
    } catch (err) {
      if (err.response && err.response.status === 404 && err.response.data?.message === 'NO_DOCS_FOR_COST_CENTER') {
        setRows([]);
        setTotaleImponibile(0);
        setTotaleConIva(0);
        setTotalePagato(0);
        setMessage('Nessun fornitore trovato per il centro di costo.');
      } else if (err.response && err.response.status === 401) {
        setMessage('Autorizzazione OAuth richiesta per visualizzare i fornitori.');
        window.alert("Autorizza l'app su Fatture in Cloud per procedere.");
      } else {
        console.error('Errore refresh fornitori', err);
      }
    } finally {
      setLoading(false);
    }
  }, [cantiereId]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await fornitoriGetCentro(cantiereId);
        setCentroCosto(res.data.centro_costo);
        await refresh(false);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          const centro = window.prompt('Inserisci centro di costo per questo cantiere');
          if (centro) {
            await fornitoriSaveCentro(cantiereId, centro);
            setCentroCosto(centro);
            await refresh(true);
          }
        }
      }
    };
    init();
  }, [cantiereId, refresh]);

  const cambiaCentro = async () => {
    const nuovo = window.prompt('Modifica centro di costo', centroCosto);
    if (nuovo) {
      await fornitoriSaveCentro(cantiereId, nuovo);
      setCentroCosto(nuovo);
      await refresh(true);
    }
  };

  const filteredRows = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.fornitore && r.fornitore.toLowerCase().includes(term));
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const sortableRows = [...filteredRows];
    if (sortConfig.key) {
      sortableRows.sort((a, b) => {
        const { key, direction } = sortConfig;
        const multiplier = direction === 'asc' ? 1 : -1;
        if (['imponibile_totale', 'totale_con_iva', 'totale_pagato'].includes(key)) {
          return (a[key] - b[key]) * multiplier;
        }
        const valA = (a[key] || '').toString().toLowerCase();
        const valB = (b[key] || '').toString().toLowerCase();
        if (valA < valB) return -1 * multiplier;
        if (valA > valB) return 1 * multiplier;
        return 0;
      });
    }
    return sortableRows;
  }, [filteredRows, sortConfig]);

  useEffect(() => {
    const imp = filteredRows.reduce((s, r) => s + r.imponibile_totale, 0);
    const conIva = filteredRows.reduce((s, r) => s + r.totale_con_iva, 0);
    const pag = filteredRows.reduce((s, r) => s + r.totale_pagato, 0);
    setTotaleImponibile(imp);
    setTotaleConIva(conIva);
    setTotalePagato(pag);
  }, [filteredRows]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <strong className="me-2">Centro di costo:</strong>
        <span className="me-3 fw-bold">{centroCosto}</span>
        <button className="btn btn-secondary btn-sm" onClick={cambiaCentro}>Modifica</button>
      </div>
      <div className="d-flex justify-content-end mb-2">
        <div className="me-3"><strong>Totale imponibile:</strong> {totaleImponibile.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
        <div className="me-3"><strong>Totale con IVA:</strong> {totaleConIva.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
        <div><strong>Totale pagato:</strong> {totalePagato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
      </div>
      <div className="mb-3 d-flex">
        <button
          className="btn btn-primary me-3"
          onClick={() => refresh(true)}
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Aggiornamento...
            </>
          ) : (
            'Aggiorna'
          )}
        </button>
        <input
          type="text"
          className="form-control"
          placeholder="Cerca..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading && (
        <div className="progress mb-3">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            style={{ width: '100%' }}
          ></div>
        </div>
      )}
      {message && <div className="alert alert-info">{message}</div>}
      <div className="custom-table-fornitori">
        <div className="custom-table-header-fornitori">
          <div onClick={() => requestSort('fornitore')} className="sortable-fornitori">Fornitore {getSortArrow('fornitore')}</div>
          <div onClick={() => requestSort('imponibile_totale')} className="sortable-fornitori">Totale imponibile {getSortArrow('imponibile_totale')}</div>
          <div onClick={() => requestSort('totale_con_iva')} className="sortable-fornitori">Totale con IVA {getSortArrow('totale_con_iva')}</div>
          <div onClick={() => requestSort('totale_pagato')} className="sortable-fornitori">Totale pagato {getSortArrow('totale_pagato')}</div>
        </div>
        {sortedRows.map((r, index) => (
          <div key={`${r.fornitore || 'no-name'}-${index}`} className="custom-table-row-fornitori">
            <div className="fornitore-cell-fornitori" title={r.fornitore}>{r.fornitore}</div>
            <div>{r.imponibile_totale.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
            <div>{r.totale_con_iva.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
            <div>{r.totale_pagato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FornitoriTable;
